import { ApolloError } from '@apollo/client'
import { isValid, parseISO } from 'date-fns'
import {
  add,
  always,
  append,
  assoc,
  descend,
  equals,
  evolve,
  max,
  omit,
  prop,
  propEq,
  sort,
  toPairs,
  update,
} from 'ramda'
import React, { Reducer, useEffect, useReducer } from 'react'

import { GetCustomerOrdersQuery } from '../../../../graphql/camel'
import {
  GetCustomerOrdersQueryOrders,
  ReorderContext,
  ReorderContextValue,
  ReorderDispatchAction,
  SortedItem,
} from '../../../../lib/orders/context'
import { logAndCaptureMessage } from '../../../../utils/errorTools'
import { alwaysUnlessEquals } from '../../../../utils/ramda'

interface ItemHistoryEntry {
  date: Date
  orderId: string
  quantity: number
}

type HistoricalItems = Readonly<{
  [sku: string]: ItemHistoryEntry[]
}>

export type State = Omit<ReorderContextValue, 'dispatch'> & {
  readonly items: HistoricalItems
  readonly orderIds: string[]
}

const initialState: State = {
  items: {},
  orderIds: [],
  itemsByDate: [],
  itemsByCount: [],
  loading: true,
  error: undefined,
}

type CustomerOrder = NonNullable<GetCustomerOrdersQuery['getCustomerOrders']>['orders'][number]
type CustomerOrderItem = NonNullable<CustomerOrder>['items'][number]

const reduceCustomerOrderItem =
  ({ date, orderId }: { date: Date; orderId: string }) =>
  (acc: HistoricalItems, item: CustomerOrderItem): HistoricalItems => {
    if (!item) {
      return acc
    }
    const { quantity, sku } = item
    const { [sku]: existingEntries } = acc
    if (!existingEntries) {
      return assoc(
        sku,
        [
          {
            date,
            orderId,
            quantity,
          },
        ],
        acc,
      )
    }

    const isOrderIdInEntries = existingEntries.some((entry) => entry.orderId === orderId)
    return isOrderIdInEntries
      ? acc
      : assoc(sku, append({ date, orderId, quantity }, existingEntries), acc)
  }

function reduceCustomerOrder(acc: HistoricalItems, order: CustomerOrder): HistoricalItems {
  if (!order) {
    return acc
  }
  const { date: isoDate, items, orderId } = order
  const date = parseISO(isoDate)
  if (!isValid(date)) {
    logAndCaptureMessage(
      `[ReorderContextProvider] invalid date "${isoDate}" (orderId: ${orderId})`,
      'warning',
    )
    return acc
  }
  return items.reduce(reduceCustomerOrderItem({ date, orderId }), acc)
}

function reduceHistoricalItemEntry(acc: SortedItem, entry: ItemHistoryEntry): SortedItem {
  const { date, quantity } = entry
  return evolve(
    {
      date: max(date),
      count: add(quantity),
    },
    acc,
  )
}

function reduceHistoricalItem(
  acc: SortedItem[],
  [sku, entries]: [string, HistoricalItems[string]],
): SortedItem[] {
  const newItem = entries.reduce(reduceHistoricalItemEntry, { sku, date: new Date(0), count: 0 })
  const existingItemIndex = acc.findIndex(propEq('sku', sku))
  if (existingItemIndex < 0) {
    return append(newItem, acc)
  }
  return equals(acc[existingItemIndex], newItem) ? acc : update(existingItemIndex, newItem, acc)
}

const reducer: Reducer<State, ReorderDispatchAction> = (prevState, action) => {
  if (action.type === 'customerOrdersUpdated') {
    const { orders } = action
    if (!orders || !orders.length) {
      return {
        ...prevState,
        loading: false,
        error: undefined,
      }
    }
    const items = orders.reduce(reduceCustomerOrder, prevState.items)
    const unsortedItems = toPairs(items).reduce(reduceHistoricalItem, [])
    const itemsByDate = sort(descend(prop('date')), unsortedItems)
    const itemsByCount = sort(descend(prop('count')), unsortedItems)

    // use evolve to update state, replacing values when changed
    return evolve(
      {
        items: always(items),
        itemsByDate: alwaysUnlessEquals(itemsByDate),
        itemsByCount: alwaysUnlessEquals(itemsByCount),
        loading: always(false),
        error: always(undefined),
      },
      prevState,
    )
  }
  if (action.type === 'customerOrdersError') {
    return {
      ...prevState,
      loading: false,
      error: action.error,
    }
  }
  return prevState
}

export interface ReorderContextProviderProps {
  children: React.ReactNode
  // optional props supplying data directly (as opposed to via dispatch())
  orders?: GetCustomerOrdersQueryOrders
  error?: ApolloError | Error
}

const ReorderContextProvider: React.FC<ReorderContextProviderProps> = ({
  children,
  error,
  orders,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    if (!orders) {
      return
    }
    dispatch({ type: 'customerOrdersUpdated', orders })
  }, [orders])

  useEffect(() => {
    if (!error) {
      return
    }
    dispatch({ type: 'customerOrdersError', error })
  }, [error])

  const reorderContext = { ...omit(['items', 'orderIds'], state), dispatch }

  return <ReorderContext.Provider value={reorderContext}>{children}</ReorderContext.Provider>
}

export default ReorderContextProvider
